export const DEVOPS_COURSES = [
  {
    id: 'devops-foundations',
    title: 'DevOps Foundations: Docker & Kubernetes',
    description: 'Learn containerization with Docker and orchestration with Kubernetes. Build, ship, and run applications reliably.',
    level: 'beginner',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic command line skills', 'Git fundamentals'],
    learningOutcomes: [
      'Containerize applications with Docker',
      'Write efficient Dockerfiles and manage images',
      'Use Docker Compose for local development',
      'Deploy and scale apps on Kubernetes (k8s)',
      'Understand Pods, Deployments, Services, and Ingress',
    ],
    modules: [
      {
        id: 'docker-k8s-intro',
        title: 'Intro to Docker and Kubernetes',
        description: 'Foundational concepts, why containers, and how Kubernetes orchestrates at scale.',
        content: `# Docker & Kubernetes Foundations

## The Container Revolution

### Why Containers Matter
- **Consistency**: "Works on my machine" problem solved
- **Portability**: Run anywhere - dev, staging, production
- **Efficiency**: Lightweight compared to VMs
- **Scalability**: Scale up/down rapidly
- **Isolation**: Process and filesystem separation

### Traditional vs. Container Architecture
\`\`\`
Traditional Deployment:
Physical Server → OS → App Dependencies → App

Container Deployment:
Physical Server → OS → Container Runtime → Container → App
\`\`\`

## Docker Fundamentals

### Core Docker Concepts
- **Image**: Read-only template with application code
- **Container**: Running instance of an image
- **Registry**: Storage for Docker images (Docker Hub, ECR)
- **Volume**: Persistent data storage
- **Network**: Container communication

### Dockerfile Best Practices
\`\`\`dockerfile
# Multi-stage build for smaller images
FROM node:18-alpine AS builder
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production

FROM node:18-alpine AS runtime
# Create non-root user for security
RUN addgroup -g 1001 -S nodejs
RUN adduser -S nextjs -u 1001

WORKDIR /app
COPY --from=builder /app/node_modules ./node_modules
COPY . .

# Change ownership to non-root user
RUN chown -R nextjs:nodejs /app
USER nextjs

EXPOSE 3000
CMD ["node", "server.js"]
\`\`\`

### Docker Compose for Development
\`\`\`yaml
version: '3.8'
services:
  app:
    build: .
    ports:
      - "3000:3000"
    environment:
      - NODE_ENV=development
    volumes:
      - .:/app
      - /app/node_modules
    depends_on:
      - db
      
  db:
    image: postgres:15-alpine
    environment:
      POSTGRES_DB: myapp
      POSTGRES_USER: user
      POSTGRES_PASSWORD: password
    volumes:
      - postgres_data:/var/lib/postgresql/data
      
volumes:
  postgres_data:
\`\`\`

## Kubernetes Architecture

### Cluster Components
- **Master Node**: API Server, Scheduler, Controller Manager, etcd
- **Worker Nodes**: kubelet, kube-proxy, Container Runtime
- **Pods**: Smallest deployable units
- **Services**: Stable network endpoints
- **Ingress**: External access management

### Essential Kubernetes Resources
\`\`\`yaml
# Deployment - manages replica sets and pods
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
    spec:
      containers:
      - name: web
        image: myapp:v1.0.0
        ports:
        - containerPort: 3000
        resources:
          requests:
            memory: "128Mi"
            cpu: "100m"
          limits:
            memory: "256Mi"
            cpu: "200m"
---
# Service - provides stable networking
apiVersion: v1
kind: Service
metadata:
  name: web-service
spec:
  selector:
    app: web-app
  ports:
  - port: 80
    targetPort: 3000
  type: LoadBalancer
\`\`\`

### Kubernetes Operations
- **Scaling**: \`kubectl scale deployment web-app --replicas=5\`
- **Rolling Updates**: \`kubectl set image deployment/web-app web=myapp:v2.0.0\`
- **Health Checks**: Liveness and readiness probes
- **Configuration**: ConfigMaps and Secrets

## Local Development Setup

### Development Workflow
1. **Build**: \`docker build -t myapp:latest .\`
2. **Test**: \`docker run -p 3000:3000 myapp:latest\`
3. **Compose**: \`docker-compose up -d\`
4. **Deploy**: \`kubectl apply -f k8s/\`
5. **Monitor**: \`kubectl logs -f deployment/web-app\`

### Debugging Commands
\`\`\`bash
# Docker debugging
docker ps                    # List running containers
docker logs <container-id>   # View logs
docker exec -it <id> /bin/sh # Shell into container

# Kubernetes debugging
kubectl get pods             # List pods
kubectl describe pod <name>  # Pod details
kubectl logs <pod-name>      # Pod logs
kubectl exec -it <pod> sh    # Shell into pod
\`\`\``,
        duration: '45 minutes',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '45 minutes',
        objectives: [
          'Explain benefits of containerization',
          'Build and run a Docker image',
          'Deploy a simple app to Kubernetes locally',
        ],
        codeExamples: [
          {
            title: 'Production-Ready Dockerfile',
            language: 'dockerfile',
            code: `# Multi-stage build for Node.js application
FROM node:18-alpine AS dependencies
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production && npm cache clean --force

FROM node:18-alpine AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

FROM node:18-alpine AS runtime
# Create non-root user
RUN addgroup -g 1001 -S nodejs && adduser -S nextjs -u 1001

WORKDIR /app

# Copy built application
COPY --from=build --chown=nextjs:nodejs /app/dist ./dist
COPY --from=dependencies --chown=nextjs:nodejs /app/node_modules ./node_modules
COPY --chown=nextjs:nodejs package*.json ./

# Security: run as non-root user
USER nextjs

EXPOSE 3000

# Health check
HEALTHCHECK --interval=30s --timeout=3s --start-period=5s --retries=3 \\
  CMD curl -f http://localhost:3000/health || exit 1

CMD ["node", "dist/server.js"]`,
            explanation: 'Demonstrates multi-stage builds, security best practices, and health checks for production containers.'
          }
        ],
        practice: {
          task: 'Containerize a full-stack application (React frontend + Node.js API + PostgreSQL) and deploy to a local Kubernetes cluster with proper service discovery and persistent storage.',
          starter: `# Basic application structure
/app
  /frontend (React app)
  /backend (Node.js API)
  package.json
  
# Your task: Create production-ready containers and K8s manifests`,
          solution: `# frontend/Dockerfile
FROM node:18-alpine AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

FROM nginx:alpine
COPY --from=build /app/build /usr/share/nginx/html
COPY nginx.conf /etc/nginx/nginx.conf
EXPOSE 80
CMD ["nginx", "-g", "daemon off;"]

# backend/Dockerfile
FROM node:18-alpine AS dependencies
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production

FROM node:18-alpine
RUN addgroup -g 1001 -S nodejs && adduser -S nodejs -u 1001
WORKDIR /app
COPY --from=dependencies --chown=nodejs:nodejs /app/node_modules ./node_modules
COPY --chown=nodejs:nodejs . .
USER nodejs
EXPOSE 3000
CMD ["node", "server.js"]

# k8s/postgres.yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: postgres
spec:
  replicas: 1
  selector:
    matchLabels:
      app: postgres
  template:
    metadata:
      labels:
        app: postgres
    spec:
      containers:
      - name: postgres
        image: postgres:15-alpine
        env:
        - name: POSTGRES_DB
          value: "myapp"
        - name: POSTGRES_USER
          value: "user"
        - name: POSTGRES_PASSWORD
          valueFrom:
            secretKeyRef:
              name: postgres-secret
              key: password
        volumeMounts:
        - name: postgres-storage
          mountPath: /var/lib/postgresql/data
      volumes:
      - name: postgres-storage
        persistentVolumeClaim:
          claimName: postgres-pvc
---
apiVersion: v1
kind: Service
metadata:
  name: postgres-service
spec:
  selector:
    app: postgres
  ports:
  - port: 5432
    targetPort: 5432

# k8s/backend.yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: backend
spec:
  replicas: 3
  selector:
    matchLabels:
      app: backend
  template:
    metadata:
      labels:
        app: backend
    spec:
      containers:
      - name: backend
        image: myapp-backend:latest
        ports:
        - containerPort: 3000
        env:
        - name: DATABASE_URL
          value: "postgresql://user:password@postgres-service:5432/myapp"
        livenessProbe:
          httpGet:
            path: /health
            port: 3000
          initialDelaySeconds: 30
          periodSeconds: 10
        readinessProbe:
          httpGet:
            path: /ready
            port: 3000
          initialDelaySeconds: 5
          periodSeconds: 5
---
apiVersion: v1
kind: Service
metadata:
  name: backend-service
spec:
  selector:
    app: backend
  ports:
  - port: 80
    targetPort: 3000`,
          hints: [
            'Use multi-stage builds to optimize image sizes',
            'Implement health checks for both liveness and readiness',
            'Use ConfigMaps for configuration and Secrets for sensitive data',
            'Set up persistent volumes for database storage',
            'Configure proper resource limits and requests',
            'Use non-root users in containers for security',
            'Test service discovery between frontend, backend, and database'
          ],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'devops-q1',
              question: 'Which Kubernetes resource maintains the desired number of pod replicas?',
              options: ['Service', 'Deployment', 'ConfigMap', 'Ingress'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'devops',
  },
  {
    id: 'cicd-pipeline-mastery',
    title: 'CI/CD Pipeline Mastery',
    description: 'Master continuous integration and deployment with Jenkins, GitHub Actions, and modern pipeline strategies.',
    level: 'intermediate',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Git fundamentals', 'Basic Docker knowledge', 'Understanding of software development lifecycle'],
    learningOutcomes: [
      'Design and implement robust CI/CD pipelines',
      'Master GitHub Actions and Jenkins pipeline configurations',
      'Implement automated testing strategies in pipelines',
      'Deploy applications using various deployment strategies',
      'Monitor and optimize pipeline performance',
    ],
    modules: [
      {
        id: 'pipeline-fundamentals',
        title: 'CI/CD Pipeline Fundamentals',
        description: 'Understanding continuous integration, delivery, and deployment principles.',
        content: `# CI/CD Pipeline Fundamentals\n\n## Core Concepts\n- Continuous Integration vs Delivery vs Deployment\n- Pipeline stages: Build, Test, Deploy\n- Artifact management and versioning\n\n## Pipeline Design Patterns\n- Branching strategies (GitFlow, GitHub Flow)\n- Environment promotion strategies\n- Rollback and recovery mechanisms\n\n## Best Practices\n- Fast feedback loops\n- Fail-fast principles\n- Immutable deployments\n- Security scanning integration`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Design effective CI/CD workflows',
          'Implement branching strategies',
          'Configure automated quality gates',
        ],
        practice: {
          task: 'Design a complete CI/CD workflow for a multi-service application.',
          hints: ['Consider environment-specific configurations', 'Include security scanning steps'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'cicd-q1',
              question: 'What is the primary benefit of continuous integration?',
              options: ['Faster deployments', 'Early detection of integration issues', 'Reduced costs', 'Better documentation'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'github-actions-advanced',
        title: 'GitHub Actions Advanced Workflows',
        description: 'Create sophisticated workflows with GitHub Actions, custom actions, and advanced features.',
        content: `# GitHub Actions Advanced\n\n## Workflow Components\n- Events, Jobs, Steps, and Actions\n- Matrix builds and conditional execution\n- Secrets and environment variables\n\n## Custom Actions\n- JavaScript actions\n- Docker container actions\n- Composite actions\n\n## Advanced Features\n- Reusable workflows\n- Environment protection rules\n- Deployment approvals\n- Self-hosted runners`,
        duration: '120 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Create complex GitHub Actions workflows',
          'Build custom actions',
          'Implement security best practices',
        ],
        practice: {
          task: 'Build a custom GitHub Action for automated security scanning.',
          hints: ['Use Docker container action', 'Include proper error handling'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'github-actions-q1',
              question: 'Which GitHub Actions feature allows you to reuse workflows across repositories?',
              options: ['Matrix strategy', 'Reusable workflows', 'Composite actions', 'Self-hosted runners'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'devops',
  },
  {
    id: 'cloud-native-devops',
    title: 'Cloud-Native DevOps',
    description: 'Advanced cloud-native practices including service mesh, observability, and chaos engineering.',
    level: 'advanced',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Kubernetes experience', 'Cloud platform basics', 'Microservices architecture'],
    learningOutcomes: [
      'Implement service mesh architectures',
      'Design cloud-native observability solutions',
      'Apply chaos engineering principles',
      'Optimize cloud-native application performance',
      'Manage complex distributed systems',
    ],
    modules: [
      {
        id: 'service-mesh-istio',
        title: 'Service Mesh with Istio',
        description: 'Implement and manage service mesh for microservices communication.',
        content: `# Service Mesh with Istio\n\n## Service Mesh Fundamentals\n- Service-to-service communication\n- Traffic management and load balancing\n- Security policies and mTLS\n\n## Istio Components\n- Envoy proxy\n- Pilot, Citadel, Galley\n- Istio Gateway and VirtualService\n\n## Advanced Features\n- Circuit breakers and retries\n- Distributed tracing\n- Canary deployments\n- Multi-cluster mesh`,
        duration: '150 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '150 minutes',
        objectives: [
          'Deploy and configure Istio service mesh',
          'Implement traffic management policies',
          'Set up security and observability',
        ],
        practice: {
          task: 'Deploy a microservices application with Istio service mesh and implement canary deployment.',
          hints: ['Use Istio Gateway for ingress', 'Configure destination rules for traffic splitting'],
          estimatedTime: 180,
        },
        quiz: {
          questions: [
            {
              id: 'istio-q1',
              question: 'What is the primary role of Envoy proxy in Istio?',
              options: ['Service discovery', 'Load balancing', 'Sidecar proxy for traffic interception', 'Configuration management'],
              correctAnswer: 2,
            },
          ],
        },
      },
      {
        id: 'chaos-engineering',
        title: 'Chaos Engineering Practices',
        description: 'Build resilient systems through controlled failure injection and testing.',
        content: `# Chaos Engineering\n\n## Core Principles\n- Hypothesis-driven experiments\n- Minimize blast radius\n- Automate experiments\n- Learn from failures\n\n## Chaos Tools\n- Chaos Monkey\n- Litmus\n- Chaos Toolkit\n- Gremlin\n\n## Failure Scenarios\n- Network partitions\n- Resource exhaustion\n- Service dependencies\n- Infrastructure failures`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Design chaos engineering experiments',
          'Implement automated failure injection',
          'Analyze system resilience',
        ],
        practice: {
          task: 'Create chaos engineering experiments for a distributed application.',
          hints: ['Start with non-production environments', 'Monitor system behavior during experiments'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'chaos-q1',
              question: 'What is the primary goal of chaos engineering?',
              options: ['Breaking systems', 'Testing system resilience', 'Finding bugs', 'Performance optimization'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'devops',
  },
  {
    id: 'devops-monitoring-observability',
    title: 'DevOps Monitoring & Observability',
    description: 'Comprehensive monitoring, logging, and observability for modern applications and infrastructure.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic Linux administration', 'Understanding of web applications', 'Docker basics'],
    learningOutcomes: [
      'Implement comprehensive monitoring with Prometheus and Grafana',
      'Set up centralized logging with ELK stack',
      'Configure application performance monitoring',
      'Design effective alerting strategies',
      'Implement distributed tracing',
    ],
    modules: [
      {
        id: 'prometheus-grafana',
        title: 'Monitoring with Prometheus & Grafana',
        description: 'Set up metrics collection, storage, and visualization.',
        content: `# Prometheus & Grafana\n\n## Prometheus Fundamentals\n- Metrics collection and storage\n- PromQL query language\n- Service discovery\n- Alerting rules\n\n## Grafana Dashboards\n- Data source configuration\n- Dashboard design\n- Alerting integration\n- Template variables\n\n## Best Practices\n- Metric naming conventions\n- Performance optimization\n- High availability setup\n- Security considerations`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Deploy Prometheus monitoring stack',
          'Create comprehensive Grafana dashboards',
          'Configure alerting rules',
        ],
        practice: {
          task: 'Set up complete monitoring for a web application with custom metrics.',
          hints: ['Use application-specific metrics', 'Configure alert thresholds based on SLIs'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'monitoring-q1',
              question: 'What type of database does Prometheus use for storing metrics?',
              options: ['SQL database', 'Time-series database', 'NoSQL database', 'In-memory database'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'elk-stack-logging',
        title: 'Centralized Logging with ELK Stack',
        description: 'Implement log aggregation, parsing, and analysis.',
        content: `# ELK Stack Logging\n\n## Elasticsearch\n- Index management\n- Query optimization\n- Cluster configuration\n- Performance tuning\n\n## Logstash\n- Input/filter/output plugins\n- Data transformation\n- Log parsing patterns\n- Performance optimization\n\n## Kibana\n- Visualization and dashboards\n- Discover and analyze logs\n- Alert configuration\n- Security features`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '90 minutes',
        objectives: [
          'Deploy ELK stack for log management',
          'Configure log parsing and enrichment',
          'Create log analysis dashboards',
        ],
        practice: {
          task: 'Implement centralized logging for a microservices application.',
          hints: ['Use structured logging', 'Implement log correlation across services'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'elk-q1',
              question: 'Which component of the ELK stack is responsible for log collection and parsing?',
              options: ['Elasticsearch', 'Logstash', 'Kibana', 'Beats'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'devops',
  },
  {
    id: 'infrastructure-as-code-advanced',
    title: 'Infrastructure as Code Advanced',
    description: 'Advanced IaC patterns with Terraform, Pulumi, and GitOps for enterprise environments.',
    level: 'advanced',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic Terraform knowledge', 'Cloud platform experience', 'Git workflows'],
    learningOutcomes: [
      'Implement enterprise Terraform patterns',
      'Master Pulumi for multi-cloud infrastructure',
      'Apply GitOps principles to infrastructure',
      'Design scalable IaC architectures',
      'Implement infrastructure security best practices',
    ],
    modules: [
      {
        id: 'terraform-enterprise',
        title: 'Terraform Enterprise Patterns',
        description: 'Advanced Terraform patterns for large-scale infrastructure management.',
        content: `# Terraform Enterprise\n\n## Advanced Patterns\n- Module composition\n- Workspace management\n- Remote state management\n- Policy as code with Sentinel\n\n## Enterprise Features\n- Team-based access control\n- Cost estimation\n- Private module registry\n- VCS integration\n\n## Best Practices\n- State file security\n- Drift detection\n- Automated testing\n- Compliance automation`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '120 minutes',
        objectives: [
          'Implement advanced Terraform patterns',
          'Configure enterprise-grade workflows',
          'Implement policy and compliance automation',
        ],
        practice: {
          task: 'Design a multi-environment Terraform setup with proper state management and policies.',
          hints: ['Use remote backends', 'Implement proper RBAC'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'terraform-adv-q1',
              question: 'What is the primary benefit of using Terraform workspaces?',
              options: ['Code reuse', 'State isolation', 'Performance improvement', 'Cost reduction'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'gitops-argocd',
        title: 'GitOps with ArgoCD',
        description: 'Implement GitOps practices for infrastructure and application deployment.',
        content: `# GitOps with ArgoCD\n\n## GitOps Principles\n- Git as single source of truth\n- Declarative configuration\n- Automated synchronization\n- Continuous reconciliation\n\n## ArgoCD Components\n- Application CRDs\n- Sync policies\n- Health checks\n- Multi-cluster management\n\n## Advanced Features\n- App of apps pattern\n- Progressive delivery\n- Policy enforcement\n- Secret management`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Implement GitOps workflows',
          'Configure ArgoCD for multi-cluster management',
          'Set up automated deployment pipelines',
        ],
        practice: {
          task: 'Set up GitOps workflow for infrastructure and applications using ArgoCD.',
          hints: ['Use Helm charts for application packaging', 'Implement proper access controls'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'gitops-q1',
              question: 'In GitOps, what serves as the single source of truth?',
              options: ['Container registry', 'Git repository', 'Kubernetes cluster', 'CI/CD pipeline'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'devops',
  },
];
